
import React from 'react';
import { Users, Lightbulb, AlignJustify, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useLocation, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const AboutUsSection: React.FC = () => {
  const location = useLocation();
  const isAboutPage = location.pathname === '/about';
  const isIndexPage = location.pathname === '/';
  const { t, language } = useLanguage();
  
  // Define values with translations based on current language
  const values = [
    {
      icon: Users,
      title: language === 'en' ? "Empowerment" : "Empoderamiento",
      description: language === 'en' 
        ? "We believe in empowering individuals with the responsibility of decision making, trusting them to control the fate of their business."
        : "Creemos en empoderar a las personas con la responsabilidad de tomar decisiones, confiando en que puedan controlar el destino de su negocio."
    },
    {
      icon: AlignJustify,
      title: language === 'en' ? "Simplicity" : "Simplicidad",
      description: language === 'en'
        ? "We embrace a non-bureaucratic approach with minimal reporting, focusing only on important performance indicators."
        : "Adoptamos un enfoque no burocrático con informes mínimos, centrándonos solo en indicadores de rendimiento importantes."
    },
    {
      icon: TrendingUp,
      title: language === 'en' ? "Continuous Improvement" : "Mejora Continua",
      description: language === 'en'
        ? "We create the right setting for our partners to enable continuous improvement of their business and processes."
        : "Creamos el entorno adecuado para que nuestros socios puedan mejorar continuamente su negocio y sus procesos."
    },
    {
      icon: Lightbulb,
      title: language === 'en' ? "Decentralized Governance" : "Gobernanza Descentralizada",
      description: language === 'en'
        ? "Our model represents independence, responsibility, and swift decision-making, serving as a sounding board when required."
        : "Nuestro modelo representa independencia, responsabilidad y toma de decisiones rápida, sirviendo como caja de resonancia cuando sea necesario."
    }
  ];
  
  return (
    <section id="about-us" className="py-20 md:py-28 bg-white dark:bg-noa-rich-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {!isAboutPage && (
          <>
            <h2 className="font-space-grotesk text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-noa-rich-black dark:text-noa-antiflash-white mb-12">
              {language === 'en' ? "Who We" : "Quiénes"}<br />
              {language === 'en' ? "Are" : "Somos"}<span className="text-blue-600">.</span>
            </h2>
            <p className="text-xl sm:text-2xl font-light text-noa-charcoal dark:text-noa-antiflash-white/90 mb-20 max-w-3xl border-l-2 border-noa-rich-black dark:border-noa-antiflash-white pl-6">
              {language === 'en' 
                ? "We are a team of ambitious entrepreneurs and investors with deep experience in building, acquiring, and scaling B2B software companies across Latin America. We built Noa Partners to provide founders with the resources, expertise, and long-term partnership needed to accelerate growth, maximize impact, and create enduring market leaders."
                : "Somos un equipo de emprendedores e inversores ambiciosos con amplia experiencia en la construcción, adquisición y escalado de empresas de software B2B en toda América Latina. Construimos Noa Partners para proporcionar a los fundadores los recursos, la experiencia y la asociación a largo plazo necesaria para acelerar el crecimiento, maximizar el impacto y crear líderes de mercado duraderos."}
            </p>
          </>
        )}

        {/* Only show Core Principles if NOT on the index page */}
        {!isIndexPage && (
          <div>
            <h3 className="font-space-grotesk text-4xl font-bold text-noa-rich-black dark:text-noa-antiflash-white mb-16">
              {language === 'en' ? "Our Core Principles" : "Nuestros Principios Fundamentales"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div 
                  key={index}
                  className="p-6 border-t border-noa-charcoal/20 dark:border-noa-antiflash-white/20"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 mr-3 flex items-center justify-center rounded-full bg-noa-antiflash-white dark:bg-noa-charcoal">
                      <value.icon className="w-5 h-5 text-noa-charcoal dark:text-noa-antiflash-white" />
                    </div>
                    <h3 className="font-space-grotesk text-xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                      {value.title}
                    </h3>
                  </div>
                  <p className="text-noa-charcoal dark:text-noa-antiflash-white/70">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isAboutPage && (
          <div className="mt-20 flex justify-start">
            <Link to="/about">
              <Button 
                className="group bg-blue-600 hover:bg-blue-700 text-white" 
                size="lg"
              >
                {language === 'en' ? "Learn more about Noa Partners" : "Conoce más sobre Noa Partners"}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutUsSection;
